import loadable from "@loadable/component"
import { createSelector } from "@reduxjs/toolkit"
import { useCallback, useMemo } from "react"
import { useSelector } from "react-redux"

import Media from "components/ui/media"
import Shimmer from "components/ui/shimmer"
import { IS_JEWLR, isTestMode, retry } from "helpers/application"
import { enableKlarnaCheckout } from "helpers/checkout/checkout_options"
// import { isSolidGold } from "helpers/product/sku-colors"
import scrollToElement from "helpers/scroll-to-element"
import { cn } from "styles/utils"

import ProductPrice from "../product-price"

import Financing from "./financing"
import { PDP_REVIEWS_BOX_TARGETED_PRODUCTS } from "./pdp-reviews-data"
import Promo from "./promo"
import Ratings from "./ratings"

const PdpReviewsCarousel = loadable(() =>
  retry(() => import("./pdp-reviews-carousel"))
)

const selectPrice = createSelector(
  (state) => state.wizard.price_and_promo,
  (priceAndPromo) =>
    priceAndPromo && Object.keys(priceAndPromo).length > 0
      ? Math.round(priceAndPromo.price_to_display * 100)
      : 0
)

const WizardHeader = () => {
  const price = useSelector(selectPrice)
  const cartTotal = useSelector((state) =>
    state.cart.summary ? state.cart.summary.cart_total : 0
  )
  const wizardLoading = useSelector((state) => state.wizard.loading)
  const title = useSelector((state) => state.wizard.title)
  const hideMiniview = useSelector(
    (state) => state.previewImageUI.miniview.scrollHide
  )

  // const selectedSku = useSelector(
  //   (state) => state.choices && state.choices.sku && state.choices.sku.value
  // )
  // const tags = useSelector((state) => state.wizard.tags)
  // const promo = useSelector((state) => state.sale.promo)
  const styleCode = useSelector((state) => state.choices?.style_code?.value)
  // const { code, product_tag, promo_badge, badge_className } = promo || {}

  const shouldShowReviews = useMemo(
    () => PDP_REVIEWS_BOX_TARGETED_PRODUCTS.includes(styleCode),
    [styleCode]
  )

  // const enablePromoBadge =
  //   (promo_badge || code === "BESTSALE") &&
  //   (!product_tag || (tags || []).includes(product_tag))

  // const promoBadge = promo_badge
  // code === "BESTSALE"
  //   ? isSolidGold(selectedSku)
  //     ? `${sl.config.sale_key.includes("cyber-monday") ? "CYBER MONDAY" : "BLACK FRIDAY"}! Extra 15% OFF Solid Gold* | Code BESTSALE`
  //     : `${sl.config.sale_key.includes("cyber-monday") ? "CYBER MONDAY" : "BLACK FRIDAY"}! Extra 20% OFF* | Code: BESTSALE`
  //   : promo_badge

  const shouldShowFinancing =
    price > 0 &&
    (sl.config.enable_affirm ||
      (sl.config.klarna_osm_pdp &&
        enableKlarnaCheckout() &&
        cartTotal <= sl.config.klarna_max))

  const handleRatingsClick = useCallback(() => {
    scrollToElement("#product-reviews", hideMiniview ? 90 : 350)
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-reviews-count-pdp",
      })
    }
  }, [hideMiniview])

  // Track tangiblee CTA clicks
  const handleTryItOnClick = useCallback(() => {
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-try-it-on-cta-pdp",
      })
    }
  }, [])

  const financing = <Financing price={price} />
  const ratings = <Ratings handleClick={handleRatingsClick} />

  return (
    <section aria-label="Product Info" className="tablet:mb-12">
      <Promo />
      {wizardLoading ? (
        <Shimmer
          className="tablet:h-(--tablet-height) tablet-w-(--tablet-width) h-(--base-height) mb-8 w-full"
          style={{
            "--base-height": "20px",
            "--tablet-height": "24px",
            "--tablet-width": "320px",
          }}
        />
      ) : (
        <h1 className="subtitle mb-8">{title}</h1>
      )}
      <ProductPrice />
      {IS_JEWLR() && (
        <>
          {wizardLoading ? (
            <Shimmer
              className={cn(
                "h-18 tablet:m-0 mb-10 w-2/5",
                shouldShowFinancing ? "mt-40" : "mt-8"
              )}
              style={{
                "--base-height": sl.config.klarna_osm_pdp ? "38px" : "18px",
                "--tablet-height": "21px",
              }}
            />
          ) : (
            <>
              {/* {enablePromoBadge && (
                <div
                  className={cn(
                    "bg-jade leading-12 rounded-xs text-black-print text-12 mt-8 inline-flex items-center justify-center p-8 font-semibold",
                    badge_className
                  )}
                >
                  {promoBadge}
                </div>
              )} */}
              <Media lessThan="tablet">
                {shouldShowFinancing && (
                  <div className="mb-12 min-h-20">{financing}</div>
                )}
                <div className="min-h-18 mb-10 flex items-center justify-between">
                  {ratings}
                  <div
                    className="tangiblee-text-link"
                    id="tangiblee-cta"
                    onClick={handleTryItOnClick}
                    onTouchStart={handleTryItOnClick}
                  ></div>
                </div>
              </Media>
              <Media greaterThanOrEqual="tablet">
                <div className="flex min-h-20 items-center justify-between gap-24">
                  {ratings}
                  {financing}
                </div>
              </Media>
              {shouldShowReviews && <PdpReviewsCarousel />}
            </>
          )}
        </>
      )}
    </section>
  )
}

export default WizardHeader
